import type { Game } from '../types';
import { ON_MARS_SUPPORTED_PLAYER_COUNTS } from './on-mars/scoringTables';
import { vinhosDeluxeGameListing } from '../games/vinhos-deluxe/metadata';
import { laFamigliaGameListing } from '../games/la-famiglia/metadata';
import { odinGameListing } from '../games/a-feast-for-odin/metadata';
import { carcassonneGameListing } from '../games/carcassonne/metadata';
import { arkNovaGame } from './arkNova';

/**
 * Platform-agnostic catalog listing (no Metro `require`, no RN image types).
 * Native `games.ts` adds `titleImage` assets; PWA maps `logoFile` to public URLs.
 */
export type GameMeta = Omit<Game, 'titleImage'> & {
  logoFile?: string;
};

export const gamesMeta: GameMeta[] = [
  {
    id: 'nieswiadomy-umysl',
    title: 'Nieświadomy Umysł',
    subtitle: 'Podstawa + Koszmary',
    icon: '🧠',
    players: '1–4 graczy',
    playTime: '90–180 min',
    contentStatus: 'partial',
    supportedPlayerCounts: [1, 2, 3, 4],
    logoFile: 'nieswiadomy-umysl-logo.png',
    logoAccessibilityLabel: 'Logo gry Nieświadomy Umysł',
  },
  {
    id: 'spirit-island',
    title: 'Spirit Island',
    subtitle: 'Rozdarta Ziemia, Zarośla i Szpony, Pióra i Płomienie',
    icon: '🌋',
    players: '1–6 graczy',
    playTime: '90–240 min',
    contentStatus: 'partial',
    supportedPlayerCounts: [1, 2, 3, 4, 5, 6],
    logoFile: 'spirit-island-logo.png',
    logoAccessibilityLabel: 'Logo gry Spirit Island',
  },
  {
    id: 'on-mars',
    title: 'ON MARS',
    subtitle: 'Samowystarczalna instrukcja dla 3–4 graczy',
    icon: '🚀',
    players: '3–4 graczy',
    playTime: '90–180 min',
    contentStatus: 'ready',
    supportedPlayerCounts: ON_MARS_SUPPORTED_PLAYER_COUNTS,
    logoFile: 'on-mars-logo.png',
    logoAccessibilityLabel: 'Logo gry ON MARS',
  },
  {
    ...laFamigliaGameListing,
    logoFile: 'la-famiglia-logo.png',
  },
  {
    id: odinGameListing.id,
    title: odinGameListing.title,
    subtitle: odinGameListing.subtitle,
    icon: odinGameListing.icon,
    players: odinGameListing.players,
    playTime: odinGameListing.playTime,
    contentStatus: odinGameListing.contentStatus,
    logoFile: 'uczta-dla-odyna-logo.png',
    logoAccessibilityLabel: odinGameListing.logoAccessibilityLabel,
  },
  {
    id: 'diuna-imperium',
    title: 'Diuna: Imperium',
    subtitle: 'Podstawa + Nieśmiertelność',
    icon: '🏜️',
    players: '1–4 graczy',
    playTime: '60–120 min',
    contentStatus: 'partial',
    supportedPlayerCounts: [1, 2, 3, 4],
    logoFile: 'diuna-imperium-logo.png',
    logoAccessibilityLabel: 'Logo gry Diuna Imperium',
  },
  {
    id: 'weather-machine',
    title: 'Weather Machine',
    subtitle: 'Deluxe Edition',
    icon: '🌦️',
    players: '1–4 graczy',
    playTime: '120–180 min',
    contentStatus: 'partial',
    supportedPlayerCounts: [1, 2, 3, 4],
    logoFile: 'weather-machine-logo.png',
    logoAccessibilityLabel: 'Logo gry Weather Machine',
  },
  {
    id: 'barrage',
    title: 'Barrage',
    subtitle: 'Podstawa + dodatek',
    icon: '💧',
    players: '1–4 graczy',
    playTime: '60–120 min',
    contentStatus: 'partial',
    supportedPlayerCounts: [1, 2, 3, 4],
    logoFile: 'barrage-logo.png',
    logoAccessibilityLabel: 'Logo gry Barrage',
  },
  {
    ...vinhosDeluxeGameListing,
    logoFile: 'vinhos-deluxe-logo.png',
  },
  {
    id: 'voidfall',
    title: 'Voidfall',
    subtitle: 'Instrukcja dla początkujących',
    icon: '🌌',
    players: '1–4 graczy',
    playTime: '60–240 min',
    contentStatus: 'partial',
    supportedPlayerCounts: [1, 2, 3, 4],
    logoFile: 'voidfall-logo.png',
    logoAccessibilityLabel: 'Logo gry Voidfall',
  },
  {
    id: arkNovaGame.id,
    title: arkNovaGame.title,
    subtitle: arkNovaGame.subtitle,
    icon: arkNovaGame.icon,
    players: arkNovaGame.players,
    playTime: arkNovaGame.playTime,
    contentStatus: arkNovaGame.contentStatus,
    logoFile: 'ark-nova-logo.png',
    logoAccessibilityLabel: arkNovaGame.logoAccessibilityLabel,
  },
  {
    ...carcassonneGameListing,
    logoFile: 'carcassonne-logo.png',
  },
  {
    id: 'projekt-gaja',
    title: 'Projekt Gaja',
    subtitle: 'Podstawa + Zaginiona Flota',
    icon: '🪐',
    players: '1–4 graczy',
    playTime: '60–150 min',
    contentStatus: 'partial',
    supportedPlayerCounts: [1, 2, 3, 4],
    logoFile: 'projekt-gaja-logo.png',
    logoAccessibilityLabel: 'Logo gry Projekt Gaja',
  },
];

export function getGameMeta(gameId: string): GameMeta | undefined {
  return gamesMeta.find((game) => game.id === gameId);
}
